<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import GreetingInput from '../components/GreetingInput.vue';
import PhoneMenuEditor from '../components/PhoneMenuEditor.vue';
import ForwardInput from '../components/ForwardInput.vue';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

// Business hours decide which flow a call takes. Each flow has the same set of settings,
// stored under its own keys (the after-hours ones prefixed with after_hours_).
const FLOWS = ['day', 'night'];
// "direct" rings staff during hours and goes to voicemail after hours
const ROUTINGS = ['direct', 'menu', 'forward'];
const FLOW_KEYS = {
  day: {
    routing: 'routing',
    forwardTo: 'forward_to',
    forwardScreen: 'forward_screen',
    menuGreeting: 'menu_greeting',
    menuGreetingAudio: 'menu_greeting_audio_url',
    menuReadOptions: 'menu_read_options',
    menuOptions: 'menu_options',
    ringTimeout: 'ring_timeout',
    greeting: 'voicemail_greeting',
    greetingAudio: 'voicemail_greeting_audio_url',
    textEnabled: 'missed_call_text_enabled',
    text: 'missed_call_text',
  },
  night: {
    routing: 'after_hours_routing',
    forwardTo: 'after_hours_forward_to',
    forwardScreen: 'after_hours_forward_screen',
    menuGreeting: 'after_hours_menu_greeting',
    menuGreetingAudio: 'after_hours_menu_greeting_audio_url',
    menuReadOptions: 'after_hours_menu_read_options',
    menuOptions: 'after_hours_menu_options',
    ringTimeout: 'after_hours_ring_timeout',
    greeting: 'after_hours_greeting',
    greetingAudio: 'after_hours_greeting_audio_url',
    textEnabled: 'after_hours_missed_call_text_enabled',
    text: 'after_hours_missed_call_text',
  },
};

const store = useStore();
const { t } = useI18n();

const settings = ref({});
const activeFlow = ref('day');
const isSaving = ref(false);

// Uploading a greeting refreshes the inbox list, which hands us a new object with the same
// saved values. Compare by content so that refresh doesn't wipe unsaved edits like a new recording.
// Cached inbox data may predate some settings, so fill in the server's defaults for those.
watch(
  () => JSON.stringify(props.inbox.call_handling || {}),
  value => {
    const saved = JSON.parse(value);
    settings.value = {
      routing: 'direct',
      after_hours_routing: 'direct',
      after_hours_forward_screen: false,
      ...saved,
      menu_options: saved.menu_options || [],
      after_hours_menu_options: saved.after_hours_menu_options || [],
    };
  },
  { immediate: true }
);

const flowTabs = computed(() =>
  FLOWS.map(flow => ({
    key: flow,
    label: t(`INBOX_MGMT.CALL_HANDLING.FLOWS.${flow.toUpperCase()}`),
  }))
);

const keys = computed(() => FLOW_KEYS[activeFlow.value]);

// DAY and NIGHT share the same copy keys
const flowText = key =>
  t(`INBOX_MGMT.CALL_HANDLING.${activeFlow.value.toUpperCase()}.${key}`);

// After hours nothing rings unless calls are forwarded or a menu option rings staff
const showRingTime = computed(
  () =>
    activeFlow.value === 'day' ||
    settings.value.after_hours_routing !== 'direct'
);

const hoursNote = computed(() =>
  props.inbox.working_hours_enabled
    ? t('INBOX_MGMT.CALL_HANDLING.HOURS_ON')
    : t('INBOX_MGMT.CALL_HANDLING.HOURS_OFF')
);

// "Repeat the menu" is never spoken or shown, so it doesn't need a label
const isIncomplete = option =>
  (!option.label && option.action !== 'repeat') ||
  (option.action === 'forward' && !option.forward_to) ||
  (option.action === 'info' && !option.message && !option.message_audio_url);

const usesRouting = (flowKeys, routing) =>
  settings.value[flowKeys.routing] === routing;

const save = async () => {
  if (isSaving.value) return;

  const flows = Object.values(FLOW_KEYS);
  const activeOptions = flows.flatMap(flowKeys =>
    usesRouting(flowKeys, 'menu') ? settings.value[flowKeys.menuOptions] : []
  );
  if (activeOptions.some(isIncomplete)) {
    useAlert(t('INBOX_MGMT.CALL_HANDLING.MENU.INCOMPLETE'));
    return;
  }
  const forwardsNowhere = flows.some(
    flowKeys =>
      usesRouting(flowKeys, 'forward') && !settings.value[flowKeys.forwardTo]
  );
  if (forwardsNowhere) {
    useAlert(t('INBOX_MGMT.CALL_HANDLING.FORWARD.MISSING'));
    return;
  }
  // Twilio dials forwards exactly as entered, so they need the country code
  const forwardNumbers = [
    ...flows
      .filter(flowKeys => usesRouting(flowKeys, 'forward'))
      .map(flowKeys => settings.value[flowKeys.forwardTo]),
    ...activeOptions
      .filter(option => option.action === 'forward')
      .map(option => option.forward_to),
  ];
  if (!forwardNumbers.every(isPhoneE164)) {
    useAlert(t('INBOX_MGMT.CALL_HANDLING.FORWARD.FORMAT'));
    return;
  }

  isSaving.value = true;
  try {
    await store.dispatch('inboxes/setCallHandling', {
      inboxId: props.inbox.id,
      callHandling: settings.value,
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 pb-8">
    <p class="mb-0 text-body-main text-n-slate-11">{{ hoursNote }}</p>
    <TabBar
      :tabs="flowTabs"
      :initial-active-tab="FLOWS.indexOf(activeFlow)"
      @tab-changed="tab => (activeFlow = tab.key)"
    />

    <SettingsToggleSection :header="flowText('TITLE')" hide-toggle>
      <template #editor>
        <div class="grid grid-cols-1 gap-3 mb-2 md:grid-cols-3">
          <RadioCard
            v-for="routing in ROUTINGS"
            :id="`${activeFlow}-${routing}`"
            :key="routing"
            :label="flowText(routing.toUpperCase())"
            :description="flowText(`${routing.toUpperCase()}_DESCRIPTION`)"
            :is-active="settings[keys.routing] === routing"
            @select="settings[keys.routing] = routing"
          />
        </div>
        <PhoneMenuEditor
          v-if="settings[keys.routing] === 'menu'"
          :key="activeFlow"
          v-model:greeting="settings[keys.menuGreeting]"
          v-model:greeting-audio-url="settings[keys.menuGreetingAudio]"
          v-model:read-options="settings[keys.menuReadOptions]"
          v-model:options="settings[keys.menuOptions]"
          :inbox-id="inbox.id"
        />
        <SettingsFieldSection
          v-else-if="settings[keys.routing] === 'forward'"
          :label="$t('INBOX_MGMT.CALL_HANDLING.FORWARD.LABEL')"
        >
          <ForwardInput
            :key="activeFlow"
            v-model:number="settings[keys.forwardTo]"
            v-model:screen="settings[keys.forwardScreen]"
          />
        </SettingsFieldSection>
      </template>
    </SettingsToggleSection>

    <SettingsToggleSection
      :header="flowText('NO_ANSWER_TITLE')"
      :description="flowText('NO_ANSWER_DESCRIPTION')"
      hide-toggle
    >
      <template #editor>
        <SettingsFieldSection
          v-if="showRingTime"
          :label="$t('INBOX_MGMT.CALL_HANDLING.RING_TIME')"
          :help-text="$t('INBOX_MGMT.CALL_HANDLING.RING_TIME_HELP')"
        >
          <Input
            v-model="settings[keys.ringTimeout]"
            type="number"
            class="max-w-28"
          />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.CALL_HANDLING.VOICEMAIL_GREETING')"
        >
          <GreetingInput
            :key="activeFlow"
            v-model:text="settings[keys.greeting]"
            v-model:audio-url="settings[keys.greetingAudio]"
            :inbox-id="inbox.id"
          />
        </SettingsFieldSection>
      </template>
    </SettingsToggleSection>

    <SettingsToggleSection
      v-model="settings[keys.textEnabled]"
      :header="$t('INBOX_MGMT.CALL_HANDLING.MISSED_CALL_TEXT')"
      :description="flowText('MISSED_CALL_TEXT_DESCRIPTION')"
    >
      <template v-if="settings[keys.textEnabled]" #editor>
        <TextArea :key="activeFlow" v-model="settings[keys.text]" auto-height />
      </template>
    </SettingsToggleSection>

    <div>
      <Button
        :label="$t('INBOX_MGMT.CALL_HANDLING.SAVE')"
        :is-loading="isSaving"
        @click="save"
      />
    </div>
  </div>
</template>
